
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CreditCard } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PortalLayout from "@/components/portal/PortalLayout";
import { supabaseGet } from "@/utils/supabaseRest";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";

interface PaymentMethod {
  id: string;
  card_last_four: string;
  card_brand: string;
  card_expiry_month: number;
  card_expiry_year: number;
  is_default: boolean;
}

interface BillingHistory {
  id: string;
  amount: number;
  status: string;
  description: string;
  created_at: string;
}

const PortalBilling = () => {
  const { toast } = useToast();

  const { data: paymentMethods } = useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () => {
      return await supabaseGet<PaymentMethod[]>("payment_methods?select=*&order=is_default.desc");
    },
  });

  const { data: billingHistory } = useQuery({
    queryKey: ["billing-history"],
    queryFn: async () => {
      return await supabaseGet<BillingHistory[]>("billing_history?select=*&order=created_at.desc&limit=10");
    },
  });

  const handleUpdatePayment = (paymentMethodId: string) => {
    toast({
      title: "Coming soon",
      description: "Payment method updates will be available soon.",
    });
  };

  const handleAddPayment = () => {
    toast({
      title: "Coming soon",
      description: "Adding payment methods will be available soon.",
    });
  };

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Billing</h1>

          <div className="grid gap-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
              <div className="space-y-4">
                {paymentMethods?.map((method) => (
                  <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <CreditCard className="h-6 w-6 text-primary" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">•••• {method.card_last_four}</p>
                          {method.is_default && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {formatCardBrand(method.card_brand)} • Expires {method.card_expiry_month.toString().padStart(2, '0')}/{method.card_expiry_year}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => handleUpdatePayment(method.id)}>
                      Update
                    </Button>
                  </div>
                ))}
                
                <Button className="w-full" onClick={handleAddPayment}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Billing History</h2>
              <div className="space-y-4">
                {billingHistory?.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No billing history available</p>
                ) : (
                  billingHistory?.map((item) => (
                    <div key={item.id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="font-medium">{item.description}</p>
                        <p className="text-sm text-gray-500">
                          {format(new Date(item.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <p className="font-medium">${item.amount.toFixed(2)}</p>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalBilling;
